import type { ComponentProps, ReactNode, Ref } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '#core/utils/cn.ts'
import { AvatarStack } from '#ynt-design-system/components/avatar/index.ts'
import type { AvatarStackProps } from '#ynt-design-system/components/avatar/index.ts'
import { Thumbnail } from '#ynt-design-system/components/thumbnail/index.ts'
import type { ThumbnailContent } from '#ynt-design-system/components/thumbnail/index.ts'

export type BadgeType =
  | 'secondary'
  | 'brand'
  | 'success'
  | 'danger'
  | 'warning'
  | 'informative'
  | 'teal'
  | 'pink'

const TYPE_CLASS: Record<BadgeType, string> = {
  secondary: 'ynt-badge-secondary',
  brand: 'ynt-badge-brand',
  success: 'ynt-badge-success',
  danger: 'ynt-badge-danger',
  warning: 'ynt-badge-warning',
  informative: 'ynt-badge-informative',
  teal: 'ynt-badge-teal',
  pink: 'ynt-badge-pink',
}
/** `keyboard` draws a key cap, `keyboard-ghost` the key's text alone; both ignore `type`. */
export type BadgeVariant = 'default' | 'subtle' | 'indicator' | 'keyboard' | 'keyboard-ghost'
export type BadgeShape = 'default' | 'pill'
/** `small` (20) sits inside another control, e.g. a Tab. */
export type BadgeSize = 'default' | 'small'

export interface BadgeProps extends Omit<ComponentProps<'span'>, 'ref'> {
  type?: BadgeType
  variant?: BadgeVariant
  shape?: BadgeShape
  size?: BadgeSize
  /** An extra-small thumbnail in place of `leadingIcon`; ignored on the indicator variant. */
  thumbnail?: ThumbnailContent
  /** Small avatars in place of `leadingIcon`; only on the default variant of secondary and brand. */
  avatarStack?: Pick<AvatarStackProps, 'people' | 'max'>
  leadingIcon?: LucideIcon
  trailingIcon?: LucideIcon
  ref?: Ref<HTMLSpanElement>
  children?: ReactNode
}

export function Badge({
  type = 'secondary',
  variant = 'default',
  shape = 'default',
  size = 'default',
  thumbnail,
  avatarStack,
  leadingIcon: Leading,
  trailingIcon: Trailing,
  className,
  children,
  ref,
  role,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...rest
}: BadgeProps) {
  const hasLabel = children !== undefined && children !== null && children !== false
  const showAvatarStack =
    avatarStack !== undefined && variant === 'default' && (type === 'secondary' || type === 'brand')
  const keyboard = variant === 'keyboard' || variant === 'keyboard-ghost'
  const Element = keyboard ? 'kbd' : 'span'
  return (
    <Element
      ref={ref}
      // An aria-label is prohibited on the generic span role, so a badge that names
      // itself without rendering a label takes a role that supports a name.
      role={role ?? (!hasLabel && (ariaLabel ?? ariaLabelledBy) !== undefined ? 'img' : undefined)}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={cn(
        'ynt-badge',
        TYPE_CLASS[type],
        variant === 'subtle' && 'ynt-badge-subtle',
        variant === 'indicator' && 'ynt-badge-indicator',
        keyboard && 'ynt-badge-keyboard',
        variant === 'keyboard-ghost' && 'ynt-badge-keyboard-ghost',
        size === 'small' && 'ynt-badge-small',
        shape === 'pill' && 'ynt-badge-pill',
        className,
      )}
      {...rest}
    >
      {variant === 'indicator' ? (
        <span className="ynt-badge-dot" />
      ) : showAvatarStack ? (
        <AvatarStack {...avatarStack} size="extra-small" className="ynt-badge-avatar-stack" />
      ) : thumbnail ? (
        <Thumbnail
          {...thumbnail}
          size="extra-small"
          shape={shape === 'pill' ? 'circle' : thumbnail.shape}
          className="ynt-badge-thumbnail"
          aria-hidden="true"
        />
      ) : (
        Leading && <Leading className="ynt-badge-icon" aria-hidden="true" />
      )}
      {hasLabel && <span className="ynt-badge-label">{children}</span>}
      {Trailing && <Trailing className="ynt-badge-icon" aria-hidden="true" />}
    </Element>
  )
}

export default Badge
